import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { StatusBadge, LoadingSpinner } from '../../components/UI';
import { format } from 'date-fns';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://cmsapi3.vdavns.com/api').replace('/api', '');

export default function AdminApplications() {
  const { role } = useSelector(s => s.auth);
  const [apps, setApps]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [nocTypes, setNocTypes] = useState([]);
  const [depts, setDepts]     = useState([]);
  const [filters, setFilters] = useState({
    status: '', noc_type_id: '', dept_id: '', date_from: '', date_to: ''
  });

  const load = useCallback((p = 1) => {
    setLoading(true);
    setPage(p);
    const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const params = new URLSearchParams({ ...clean, page: p, limit: 20 });
    api.get('/admin/applications?' + params.toString())
      .then(r => {
        setApps(r.applications || []);
        setTotal(r.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    api.get('/noc-types/public').then(r => setNocTypes(r.noc_types || [])).catch(() => {});
    // departments - try admin route, fallback silently
    api.get('/departments/public').then(r => setDepts(r.departments || [])).catch(() => {});
    load(1);
  }, []); // eslint-disable-line

  const STATUS_OPTIONS = [
    { value: '',                    label: 'All Status' },
    { value: 'submitted',           label: 'Submitted' },
    { value: 'approved_by_related', label: 'Dept Approved' },
    { value: 'objection_by_related',label: 'Objection (Dept)' },
    { value: 'rejected_by_related', label: 'Dept Rejected' },
    { value: 'approved_final',      label: 'Final Approved' },
    { value: 'objection_by_final',  label: 'Objection (Final)' },
    { value: 'rejected_final',      label: 'Final Rejected' },
    { value: 'pulled',              label: 'Pulled' },
  ];

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📂 All Applications</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Complete record of all NOC applications in the system
          </p>
        </div>
        <div className="text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-4 py-2 rounded-xl">
          Total: {total}
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <select
            className="form-select text-sm"
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <select
            className="form-select text-sm"
            value={filters.noc_type_id}
            onChange={e => setFilters(f => ({ ...f, noc_type_id: e.target.value }))}>
            <option value="">All NOC Types</option>
            {nocTypes.map(n => <option key={n.id} value={n.id}>{n.noc_name}</option>)}
          </select>

          <select
            className="form-select text-sm"
            value={filters.dept_id}
            onChange={e => setFilters(f => ({ ...f, dept_id: e.target.value }))}>
            <option value="">All Departments</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.dept_name}</option>)}
          </select>

          <input type="date" className="form-input text-sm"
            value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />

          <input type="date" className="form-input text-sm"
            value={filters.date_to}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />

          <div className="flex gap-2">
            <button onClick={() => load(1)} className="btn-primary flex-1 text-sm">
              🔍 Search
            </button>
            <button
              onClick={() => {
                setFilters({ status:'', noc_type_id:'', dept_id:'', date_from:'', date_to:'' });
                setTimeout(() => load(1), 100);
              }}
              className="btn-secondary text-sm px-3"
              title="Clear filters">
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner size="lg" className="py-20" />
      ) : apps.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-4">📂</p>
          <p className="text-lg font-semibold text-gray-700">No Applications Found</p>
          <p className="text-sm text-gray-400 mt-1">
            {Object.values(filters).some(Boolean)
              ? 'No records match the selected filters. Try clearing filters.'
              : 'No applications have been submitted yet.'}
          </p>
          {Object.values(filters).some(Boolean) && (
            <button
              onClick={() => { setFilters({ status:'', noc_type_id:'', dept_id:'', date_from:'', date_to:'' }); setTimeout(() => load(1), 100); }}
              className="btn-secondary mt-4 text-sm">
              Clear All Filters
            </button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          {/* Count bar */}
          <div className="px-4 py-2.5 bg-gray-50 border-b flex items-center justify-between text-xs text-gray-500">
            <span>Showing {apps.length} of {total} applications</span>
            <span>Page {page} of {totalPages || 1}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>App No</th>
                  <th>NOC Type</th>
                  <th>Applicant</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th>Current Dept</th>
                  <th>Submitted</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app, idx) => (
                  <tr key={app.id}>
                    <td className="text-xs text-gray-400">{(page - 1) * 20 + idx + 1}</td>
                    <td className="font-mono text-xs text-blue-600 font-semibold">
                      {app.application_no}
                    </td>
                    <td className="text-sm font-medium text-gray-800">{app.noc_name}</td>
                    <td className="text-sm text-gray-700">{app.user_name}</td>
                    <td className="text-xs text-gray-500">{app.mobile}</td>
                    <td><StatusBadge status={app.status} /></td>
                    <td className="text-xs text-gray-500">
                      {app.current_dept || (
                        app.status === 'approved_final' ? '✅ Completed' :
                        app.status.includes('rejected') ? '❌ Rejected' :
                        app.status === 'pulled' ? '🚫 Pulled' : '—'
                      )}
                    </td>
                    <td className="text-xs text-gray-400">
                      {app.submitted_at
                        ? format(new Date(app.submitted_at), 'dd/MM/yyyy')
                        : '—'}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Link
                          to={role === 'developer'
                            ? `/developer/applications/${app.id}`
                            : `/admin/applications/${app.id}`}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors">
                          View →
                        </Link>
                        {app.status === 'approved_final' && app.final_noc_pdf &&
                          app.final_noc_pdf.endsWith('.pdf') && (
                          <a
                            href={`${API_BASE}${app.final_noc_pdf}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-xs font-medium text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-full transition-colors">
                            📄 NOC
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
              <button
                onClick={() => load(page - 1)}
                disabled={page === 1}
                className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">
                ← Prev
              </button>

              <div className="flex gap-1">
                {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => load(p)}
                    className={`w-8 h-8 rounded text-xs font-medium transition-colors
                      ${page === p
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'}`}>
                    {p}
                  </button>
                ))}
                {totalPages > 10 && <span className="text-gray-400 text-xs px-1 self-center">...</span>}
              </div>

              <button
                onClick={() => load(page + 1)}
                disabled={page === totalPages}
                className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}